import { DatePipe } from '@angular/common';
import { Injectable, NgIterable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, lastValueFrom, Subject } from 'rxjs';
import { BusinessModel, BusinessUnitModel, ContactModel, EBusinessType, EContactFieldType, EContactType, MaterialModel, UserModel } from '../../@models';
import { BaseProductModel } from '../../@models/erp/baseProduct';
import { CompilationModel, ErpDateClassType, IPalletRef, isErpDate, PackingConditions } from '../../@models/erp/compilation';
import { CompilationDataModel, CompilationTypeData, DocConfig, IssuerData } from '../../@models/erp/compilationData';
import { CoreModel } from '../../@models/erp/core';
import { EBoxLabelPriority, EProductType, FinishedProductModel, NameTable } from '../../@models/erp/finishedProduct';
import { RegionModel } from '../../@models/region';
import { distinctFromObjectArray, isEmpty, latestVersion, mongoId, sanitizeDateToString, toEUR } from '../common/global';
import { ApiService } from './api.service';
import { OcSpoLineItemMap, TreeService } from './tree.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component } from '@angular/core';
import { SpoBoxModel } from '../../@models/erp/spoBox';
import { PackingConditionsComponent } from '../../pages/erp/document/packing-conditions/packing-conditions.component';
import { EWarehousingEntryType, WarehousingModel } from '../../@models/warehousing';
import * as moment from 'moment';
import { ContactUtil } from '../../pages/erp/shared/doc-form-address-selector/doc-form-address-selector.component';
import { AssociatedPfspo } from '../../@models/erp/lineItem';
import { DocumentFinishedProductSelectorComponent } from '../../pages/erp/document/document-select/document-finished-product-selector.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { AuthGuardService } from './auth-guard.service';
import { CommonDialogComponent, NrkDialogType } from '../../pages/erp/shared/common-dialog/common-dialog.component';
import { PriceMatrixModel } from '../../@models/erp/priceMatrix';

export interface IDropdownOptions {
  id: string;
  name: string;
}

export interface IPositionMap {
  [code: string]: { [internalPos: number] : /* pos */ number }
}

@Injectable()
export class SharedDataService  {

  public totalNet: number;
  // public areBoxesLoading: boolean[] = [];
  // public boxesAreLoading = (push = false) => push 
  //   ? setTimeout(() => this.areBoxesLoading.push(true)) 
  //   : setTimeout(() => this.areBoxesLoading.pop());

  private _boxesAreLoading: boolean[] = [];
  public areBoxesLoading = () => this._boxesAreLoading.length > 0;
  public boxesAreLoading = (push = false) => setTimeout(() => push ? this._boxesAreLoading.push(true) : this._boxesAreLoading.pop()) 

  public reportIsLoading = false;

  // businesses
  private _businesses: BusinessModel[];
  private _businessesWithDisabled: BusinessModel[];
  private _businessOptions: IDropdownOptions[];
  public get businessesWithDisabled(): BusinessModel[] {
    return this._businessesWithDisabled;
  }
  public get businessOptions(): IDropdownOptions[] {  // dropdown / select options
    return this._businessOptions;
  }
  public get businesses(): BusinessModel[] {
    return this._businesses;
  }
  public business = {
    micamation: null,
    nrt: null,
    nrvi: null,
    bartech: null,
    nrka: null,
    nrkg: null,
    nrkj: null,
    nrkv: null,
    ceva: null,
    dhl: null,
    jerich: null,
  }
  loadBusinesses = async () => this._businesses || await this.reloadBusinesses();

  // business units
  private _businessUnits: BusinessUnitModel[];
  public businessUnitOptions: IDropdownOptions[] = [];
  public get businessUnits(): BusinessUnitModel[] {
    return this._businessUnits;
  }
  loadBusinessUnits = async () => this._businessUnits || await this.reloadBusinessUnits();

  // materials
  private _materials: MaterialModel[];
  public materialOptions: IDropdownOptions[] = [];
  public get materials(): MaterialModel[] {
    return this._materials;
  }
  loadMaterials = async () => this._materials || await this.reloadMaterials();

  // users
  private _users: UserModel[];
  public userOptions: IDropdownOptions[] = [];
  public get users(): UserModel[] {
    return this._users;
  }
  loadUsers = async () => this._users || await this.reloadUsers();
  userName = (user: UserModel) => this._users?.find(f => mongoId(f) === mongoId(user))?.name || '';
  userShort = (user: UserModel) => this._users?.find(f => mongoId(f) === mongoId(user))?.short || '';

  // contacts
  private _contacts: ContactModel[];
  public contactOptions: IDropdownOptions[] = [];
  public nrkContactOptions: IDropdownOptions[] = [];
  public get contacts(): ContactModel[] {
    return this._contacts;
  }
  loadContacts = async () => this._contacts || await this.reloadContacts();

  // cores
  private _cores: CoreModel[];
  public coreOptions: IDropdownOptions[] = [];
  public get cores(): CoreModel[] {
    return this._cores;
  }
  loadCores = async () => this._cores || await this.reloadCores();

  // base products + finished products
  private _products: any;
  public baseProductOptions: IDropdownOptions[] = [];
  public get baseProducts(): BaseProductModel[] {
    return this._products?.baseProducts;
  }
  public finishedProductOptions: IDropdownOptions[] = [];
  public get finishedProducts(): FinishedProductModel[] {
    return this._products?.finishedProducts;
  }
  public finishedProductNames: NameTable[] = [];
  loadProducts = async () => this._products || await this.reloadProducts();

  // boxes
  private _spoBoxes: SpoBoxModel[];
  public get spoBoxes(): SpoBoxModel[] {
    return  this._spoBoxes;
  }
  loadSpoBoxes = async () => this._spoBoxes || await this.reloadSpoBoxes();

  private _prices: PriceMatrixModel[];
  public get prices(): PriceMatrixModel[] {
    return this._prices;
  }
  loadPrices = async () => this._prices || await this.reloadPrices();

  private _palletReferences: IPalletRef[];
  public get palletRefs(): IPalletRef[] {
    return this._palletReferences;
  }
  loadPalletRefs = async() => this._palletReferences || await this.reloadPalletReferences();

  // position map
  private _positionMap: IPositionMap;
  public get positionMap(): IPositionMap {
    return  this._positionMap;
  }
  public getPos(code: string, internalPos: number) {
    const doc = this.positionMap[code];
    if (doc) {
      const li = doc[+internalPos];
      return li === undefined ? '' : +li + 1
    }
    return ''
  }
  loadPositionMap = async () => this._positionMap || await this.reloadPositionMap();

  // compilation data
  private _compilationData: CompilationDataModel;
  public get compilationData(): CompilationDataModel {
    return this._compilationData;
  }
  loadCompilationData = async () => this._compilationData || await this.reloadCompilationData();
  private _paymentTextOptions: IDropdownOptions[];
  public get paymentTextOptions(): IDropdownOptions[] {
    return this._paymentTextOptions;
  }
  private _termsOfPaymentOptions: IDropdownOptions[];
  public get termsOfPaymentOptions(): IDropdownOptions[] {
    return this._termsOfPaymentOptions;
  }
  private _paymentPlanTextOptions: IDropdownOptions[];
  public get paymentPlanTextOptions(): IDropdownOptions[] {
    return this._paymentPlanTextOptions;
  }
  private _palletTypeOptions: IDropdownOptions[];
  public get palletTypeOptions(): IDropdownOptions[] {
    return this._palletTypeOptions;
  }
  private _cartonBoxOptions: IDropdownOptions[];
  public get cartonBoxOptions(): IDropdownOptions[] {
    return this._cartonBoxOptions;
  }
  private _packingOptions: IDropdownOptions[];
  public get packingOptions(): IDropdownOptions[] {
    return this._packingOptions;
  }

  // compilation documents basic data
  private _compilationItems: CompilationModel[] = [];
  public get compilationItems(): CompilationModel[] {
    return this._compilationItems;
  }
  public set compilationItems(c : CompilationModel[]) {
    this._compilationItems = c;
  }

  // preloadCompilations = async () => this._compilationItems || await this.reloadCompilationItems(true);
  // loadCompilations = async () => this._compilationItems || await this.reloadCompilationItems();
  private _latestVersionItems: CompilationModel[];
  public get latestVersionItems(): CompilationModel[] {
    return this._latestVersionItems;
  }
  public set latestVersionItems(infos: CompilationModel[]) {
    this._latestVersionItems = infos;
  }

  private _regions: RegionModel[];
  public get regions(): RegionModel[] {
    return this._regions;
  }
  loadRegions = async () => this._regions || await this.reloadRegions();

  // warehousing log / messages
  private _warehousingData: WarehousingModel[];
  private _warehousingLog: WarehousingModel[];
  private _warehousingMessages: WarehousingModel[];
  public get warehousingLog(): WarehousingModel[] {
    return this._warehousingLog;
  }
  public get warehousingMessages(): WarehousingModel[] {
    return this._warehousingMessages;
  }
  loadWarehousing = async () => this._warehousingData || await this.reloadWarehousing();

  public $expiryTime = new BehaviorSubject<string>('00:00:00');

  public tcSpoMap: NgIterable<any>;

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public packingComponent: PackingConditionsComponent;

  public visibleMap: any = {}

  public readOnlyMap: any = {}

  constructor(
    private api: ApiService,
    private datePipe: DatePipe,
    private domSanitizer: DomSanitizer,
    private clipboard: Clipboard,
    private toastr: NbToastrService,
    private dialogService: NbDialogService,
    private auth: AuthGuardService,
  ) {}


  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  

  async reloadBusinesses() {
    const start = new Date().getTime();
    this._businesses = []; // avoid reloading with this.loadBusinesses()
    return new Promise<BusinessModel[]>( resolve => {
      const subscr = this.api.reloadBusiness().subscribe(b => {
        this._businessesWithDisabled = b;
        this._businesses = b.filter(f => f.active);
        this._businessOptions = b ? this._businesses.map(el => ({ id: el._id, name: el.name })) : [];

        const a = b.filter(f => f.active); // active business only for this.business

        this.business.micamation = a.find(f => /^Micamation$/i.test(f.name));
        this.business.nrt = a.find(f => /^NRT$/i.test(f.name));
        this.business.nrvi = a.find(f => /^NRVI$/i.test(f.name));
        this.business.bartech = a.find(f => /^NRBM$/i.test(f.name));
        this.business.nrka = a.find(f => /^NRKA$/i.test(f.name));
        this.business.nrkg = a.find(f => /^NRKG$/i.test(f.name));
        this.business.nrkj = a.find(f => /^NRKJ$/i.test(f.name));
        this.business.nrkv = a.find(f => /^NRKV$/i.test(f.name));
        this.business.ceva = a.find(f => /^CEVA.*Airport/i.test(f.name));
        this.business.dhl = a.find(f => /^DHL /.test(f.name));
        this.business.jerich = a.find(f => /^Jerich Transport/i.test(f.name));

        console.log('reload', this.business.jerich, this.business)
    
        subscr.unsubscribe();
        console.info('@shared-data: load businesses took: ', (new Date().getTime() - start) + 'ms');
        resolve(this._businesses);
      })
    })
  }

  async reloadBusinessUnits() {
    const start = new Date().getTime();
    this._businessUnits = []; // avoid reloading cf. reloadBusinesses()
    return new Promise<BusinessUnitModel[]>(resolve => {
      const subscr = this.api.reloadBusinessUnits().subscribe(_bu => {
        this._businessUnits = _bu;
        this.businessUnitOptions = this._businessUnits.map(el => ({ id: el._id, name: el.name }))
        subscr.unsubscribe();
        console.info('@shared-data: load business units took: ', (new Date().getTime() - start) + 'ms');
        resolve(this._businessUnits);
      });
    })
  }

  async reloadMaterials() {
    const start = new Date().getTime();
    this._materials = [];
    return new Promise<MaterialModel[]>( resolve => {
      const subscr = this.api.reloadMaterials().subscribe(mat => {
        this._materials = mat;
        this.materialOptions = this._materials.map(el => ({ id: el._id, name: el.name }))
        subscr.unsubscribe();
        console.info('@shared-data: load materials took: ', (new Date().getTime() - start) + 'ms');
        resolve(this._materials)
      })
    })
  }

  async reloadUsers() {
    const start = new Date().getTime();
    this._users = []; // avoid reloading cf. reloadBusinesses()
    this._users = await this.api.reloadUsers();
    this.userOptions = this._users.map(u => ({ id: u._id, name: u.name })).sort((a, b) => a.name.localeCompare(b.name));
    console.info('@shared-data: load users took: ', (new Date().getTime() - start) + 'ms');
    return this._users;
  }

  async reloadContacts() {
    await this.loadBusinesses();
    const start = new Date().getTime();
    this._contacts = []; // avoid reloading cf. reloadBusinesses()
    return new Promise<ContactModel[]>(resolve => {
      const subscr = this.api.reloadContacts().subscribe(c => {
        this._contacts = c;
        // this._contacts.forEach(c => c.business = this.businesses.find(f => mongoId(f) === c.business));
        console.log('contacts', this._contacts);
        this.contactOptions = this._contacts.map(el => ({ id: el._id, name: el.title }))
        this.nrkContactOptions = this._contacts
          .filter(f => f.business?.active && +f?.business.type === EBusinessType.NRK && f.title && +f.type === EContactType.Employee)
          .map(el => {
            const job = el.fields.find(f => +f.type === EContactFieldType.JobTitle)?.value;
            return ({ id: el._id, name: `${el.business?.name}: ${el.title} ${job ? '(' + job + ')' : ''}`})
          })
          .sort((a, b) => a.name.localeCompare(b.name))
        subscr.unsubscribe();
        console.info('@shared-data: load contacts took: ', (new Date().getTime() - start) + 'ms');
        resolve(this._contacts);
      })
    })
  }

  async reloadCores() {
    const start = new Date().getTime();
    this._cores = []; // avoid reloading cf. reloadBusinesses()
    this._cores = await this.api.loadCores();
    this.coreOptions = this._cores.sort((a,b) => a.name.localeCompare(b.name)).map(el => ({ id: el._id, name: el.name }))
    console.info('@shared-data: load cores took: ', (new Date().getTime() - start) + 'ms');
    return this._cores;
  }

static productsLoading: Promise<{ baseProducts: any[]; finishedProducts: any[] }> | null = null;

async reloadProducts() {

  if (SharedDataService.productsLoading) {
    return SharedDataService.productsLoading;
  }

  const start = new Date().getTime();

  SharedDataService.productsLoading = (async () => {
    try {
      const baseProducts = latestVersion(await this.api.loadBaseProducts());
      this.baseProductOptions = baseProducts
        .sort((a, b) => a.version < b.version ? 1 : -1)
        .map(el => ({ id: el._id, name: `${el.name} [${el.trial}]` }));

      const finishedProducts = latestVersion(await this.api.loadFinishedProducts());

      await this.loadBusinessUnits();
      await this.loadMaterials();
      await this.loadCores();

      finishedProducts?.forEach(fp => {
        if (fp?.product) {
          fp.product = baseProducts.find(bp => mongoId(bp) === mongoId(fp.product));
          if (fp.product) {
            fp.product.businessUnit = this.businessUnits?.find(
              bu => bu._id === mongoId(fp.product?.businessUnit)
            );
            fp.product.material = this.materials?.find(
              m => m._id === mongoId(fp.product.material)
            );
          }
        }

        let customerName = '', core = '';

        if (fp.type === EProductType.Electro) {
          customerName = fp.content.electro;
        } else {
          const name = fp.product ? fp.product['name'] + '; ' : '';
          if (fp.content.energy?.core) {
            fp.content.energy.core = this.cores?.find(
              c => c._id === mongoId(fp.content.energy.core)
            );
            core = this.getFinishedProductDimensions(fp) || '';
          }
          customerName = name + core;
        }

        const customers = Array.isArray(fp.customers) ? fp.customers : [fp.customers];
        const suppliers = Array.isArray(fp.suppliers) ? fp.suppliers : [fp.suppliers];
        const business = [...customers, ...suppliers].map(m => m && mongoId(m.id));

        this.finishedProductNames.push({
          id: fp._id,
          nrkName:
            fp.nameOverride != null && fp.nameOverride.length > 0
              ? fp.nameOverride
              : fp.product?.name,
          baseCode: fp.product?.code,
          bpName: fp.product?.name,
          trial: fp.product?.trial,
          nrkCode:
            fp.product?.businessUnit?.code +
            '-' +
            fp.product?.material?.code +
            '-' +
            fp.code,
          customerName: (fp.externalName ? fp.externalName : customerName)?.replace(/\;\s*$/, ''),
          customerCode: fp.externalCode,
          core: core,
          calcCode: fp.calcCode,
          calcName: fp.calcName,
          code: fp.code,
          business: business,
          allBusinesses: fp.allCustomers || fp.allSuppliers,
        });
      });

      this.finishedProductOptions = finishedProducts.map(el => ({
        id: el._id,
        name: el.calcName,
      }));

      console.info(
        '@shared-data: load products took: ',
        new Date().getTime() - start + 'ms'
      );

      this._products = { baseProducts, finishedProducts };
      return this._products;
    } finally {
      SharedDataService.productsLoading = null;
    }
  })();

  return SharedDataService.productsLoading;
}
  getFinishedProductDimensions(fp: FinishedProductModel): string {
    if (!fp._id) fp = this.finishedProducts?.find(f => mongoId(f) === mongoId(fp));
    return fp?.content.energy?.core?.name ? fp.content.energy.width + '-' + fp.content.energy.rollLength + '-' + fp.content.energy.core.name : null;
  }
  getFinishedProductString(fp: FinishedProductModel): string {
    if (!fp?._id) fp = this.finishedProducts?.find(f => mongoId(f) === mongoId(fp));
    return fp?.calcName ?? '';
  }

  async reloadSpoBoxes() {
    const start = new Date().getTime();
    this._spoBoxes = [];
    // this.boxesAreLoading.push(true);
    this.boxesAreLoading(true);
    this._spoBoxes = await this.api.loadSpoBoxes();
    // this.boxesAreLoading.pop();
    this.boxesAreLoading();
    console.info('@shared-data: load spo boxes took: ', (new Date().getTime() - start) + 'ms');
  }

  async reloadPrices() {
    this._prices = await this.api.loadPriceMatrix(IssuerData.get('NRKA').id);
  }

  priceIsValid(business: string | BusinessModel, finishedProduct: string | FinishedProductModel, referenceDate: Date, accounting = 0) {
    const pm = this.prices
      .find(f => 
        mongoId(f.business) === mongoId(business) 
        && mongoId(f.finishedProduct) === mongoId(finishedProduct)
        && f.accounting == accounting
      )
    // console.log('price matrix ... ', pm);
    if (!pm) return false;
    const refDate = moment(referenceDate);
    // console.log('price is valid', moment(refDate).toDate().toISOString(), (refDate.isSameOrAfter(moment(pm.price[0].validFrom), 'day') && !pm.price[0].validTo), (refDate.isSameOrAfter(moment(pm.price[0].validFrom), 'day') && refDate.isSameOrBefore(moment(pm.price[0].validTo), 'day')));
    if (refDate.isSameOrAfter(moment(pm.price[0].validFrom), 'day') && !pm.price[0].validTo) return true;
    for (let p of pm.price) {
      // console.log('price ...', 
      //   refDate,
      //   p.validTo,
      //   refDate.isSameOrAfter(moment(p.validFrom), 'day'),
      //   refDate.isSameOrBefore(moment(p.validTo), 'day'),
      //   refDate.isSameOrAfter(moment(p.validFrom), 'day') && !p.validTo,
      //   refDate.isSameOrAfter(moment(p.validFrom), 'day') && p.validTo && refDate.isSameOrBefore(moment(p.validTo), 'day') 
      // )
      if (refDate.isSameOrAfter(moment(p.validFrom), 'day') && p.validTo && refDate.isSameOrBefore(moment(p.validTo), 'day')) {
        // console.log('is valid', true)
        return true;
      }
    }
    // console.log('is valid', false)
    return false;
  }

  async reloadPalletReferences() {
    const start = new Date().getTime();
    this._palletReferences = [];
    this._palletReferences = await this.api.getPalletReferences();
    console.info('@shared-data: load pallet references took: ', (new Date().getTime() - start) + 'ms');
  }

  async reloadPositionMap() {
    const start = new Date().getTime();
    this._positionMap = await this.api.getPositionMap();
    console.log('position map: ', this.positionMap );
    console.info('@shared-data: load position map took: ', (new Date().getTime() - start) + 'ms');
  }

  async reloadCompilationData() {
    const start = new Date().getTime();
    this._compilationData = new CompilationDataModel(); // avoid reloading cf. reloadBusinesses()
    this._compilationData = await this.api.getCompilationData();

    this._paymentTextOptions = this.compilationData.paymentStore?.find(f => f.id === 'paymentText')?.data?.map(m => ({ id: m.text, name: m.text }));
    this._termsOfPaymentOptions = this.compilationData.paymentStore?.find(f => f.id === 'termsOfPayment')?.data?.map(m => ({ id: m.text, name: m.text }));
    this._paymentPlanTextOptions = this.compilationData.paymentStore?.find(f => f.id === 'paymentPlanText')?.data?.map(m => ({ id: m.text, name: m.text }));
    this._palletTypeOptions = this.compilationData.packingStore.find(f => f.id === 'palletDetails')?.data.map(m => ({ id: m.id, name: m.name }));
    this._cartonBoxOptions = this.compilationData.packingStore.find(f => f.id === 'boxDetails')?.data.filter(f => !f.disabled).map(m => ({ id: m.id, name: m.name }));
    this._packingOptions = this.compilationData.packingStore.find(f => f.id === 'finalPacking')?.data.filter(f => !f.disabled).map(m => ({ id: m.id, name: `${m.id} ${m.name}` }));
    // console.log('final packing', this.compilationData.packingStore.find(f => f.id === 'finalPacking')?.data, this.compilationData.packingStore.find(f => f.id === 'finalPacking')?.data.filter(f => !f.disabled))

    console.info('@shared-data: load compilationData took: ', (new Date().getTime() - start) + 'ms');
    return this._compilationData;
  }

  async reloadCompilationItems(from: number, len: number) {
    if (from === 0) this.reloadPositionMap();
    const start = new Date().getTime();
    const slice = await this.api.getAllVersionData(true, false, null, null, from, len)
    // console.log('len pre', this._compilationItems?.length, this._latestVersionItems?.length);
    this._compilationItems.push(...(slice as CompilationModel[]));
    this._latestVersionItems = distinctFromObjectArray(this._compilationItems, 'code');
    console.info(`@shared-data: load compilation infos; slice (${from}-${from+len}) took: ${new Date().getTime() - start} ms`);
    // console.log('len post', this._compilationItems?.length, this._latestVersionItems?.length, (slice as CompilationModel[]))
    return slice;
  }

  // async reloadCompilationItems() {
  //   const start = new Date().getTime();
  //   this._compilationItems = [];
  //   this._compilationItems = await this.api.getAllVersionData(preload) as CompilationModel[];
  //   console.info('@shared-data: load compilation infos; preload:', preload, new Date().getTime() - start);
  //   return this._compilationItems;
  // }

  // async reloadCompilationInfos(preload = false, abstractItem: AbstractItem<CompilationModel>) {
  //   console.log('@shared-data: load compilation infos');
  //   abstractItem.items = await this.api.getAllVersionData(preload) as CompilationModel[];
  //   this._compilationInfos = abstractItem.getCurrentItems(abstractItem.items, true)
  //     .sort((a, b) => a['updatedAt'] < b['updatedAt'] ? 1 : -1);
  //   return this._compilationInfos;
  // }

  async reloadRegions() {
    const start = new Date().getTime();
    return new Promise<RegionModel[]>(resolve => {
      const subscr = this.api.reloadRegions().subscribe(regions => {
        this._regions = regions;
        subscr.unsubscribe();
        console.info('@shared-data: load regions took: ', (new Date().getTime() - start) + 'ms');
        resolve(this._regions);
      })
    })
  }

  async reloadWarehousing(forced = false) {
    const start = new Date().getTime();
    let itemsLoaded = 0
    if (!this._warehousingData?.length || forced) {
      this._warehousingData = await this.api.loadWarehousingEntries();
      // console.log('warehousing data initial load: ', this._warehousingData.length);
      itemsLoaded = this._warehousingData.length;
    } else {
      const latestDate = moment.max(this._warehousingData.map(m => moment(m.timestamp))).toDate();
      const newEntries = await this.api.loadWarehousingEntries({ timestamp: { $gt: latestDate }});
      // console.log('reload warehousing: entries: ',this._warehousingData.length, ', new entries: ', newEntries.length);
      if (newEntries?.length) this._warehousingData.push(...newEntries);
      itemsLoaded = newEntries.length;
    }
    this._warehousingMessages = this._warehousingData.filter(f => f.entryType === EWarehousingEntryType.message).reverse();
    this._warehousingLog = this._warehousingData.filter(f => f.entryType === EWarehousingEntryType.documentLog).reverse();
    console.info(`@shared-data: load warehousing data (${itemsLoaded} item(s)) took: `, (new Date().getTime() - start) + 'ms');
  }

  async reloadTcSpoMap(compilation: CompilationModel) {
    const map = [];
    for (const pc of compilation?.packingConditions) {
      console.log('pc.items', pc.items);
      if (!Array.isArray(pc.items)) continue;
      for (const item of pc.items) {
        for (const tc of item.tcNr.replace(/\s/g,'').split(';').filter(notNull => notNull)) {
          if (!map.find(f => f.tc === tc)) {
            const rec = await this.api.getSpoOfTC(tc);
            map.push({
              tc: tc,
              spo: rec.spo?.join(', '),
            })
          }
        }
      }
    }
    console.log('getTcSpoMap', compilation.code, map);
    this.tcSpoMap = map;
  }

  // ========================================================================================================================

  getOptionName(opts: IDropdownOptions[], id: any) {
    return opts?.find(f => f?.id == id)?.name || '';
  }

  setCSIdentifier(doc: CompilationModel) {
    if (!doc || doc.type !== CompilationTypeData.get('CS').id) return;
    const exwDate = doc.exwJapanDate ? this.datePipe.transform(doc.exwJapanDate, 'yyMMdd') : '___';
    const manufacturer = (this.businesses.find(b => mongoId(b) === mongoId(doc.manufacturerRecipient)) || {} as any).name || '___';
    const deliverer = (this.businesses.find(b => mongoId(b) === mongoId(doc.deliveryRecipient)) || {} as any).name || '___';
    const carrierMethod = doc.meansOfTransport || '___';

    doc.internal = `${exwDate}-${manufacturer}-${deliverer}-${carrierMethod}`;
  }

  getFileDescriptions(id: string) {
    const texts = this._compilationData && this._compilationData.fileDescriptions.find(fd => fd.id === id);
    return texts && texts.data;
  }

  setDocConfigMaps(currentCompilationType: number) {
    for (const section of DocConfig.getArray()) {
      this.visibleMap[section.key] = DocConfig.checkVisible(section.key, currentCompilationType);
      this.readOnlyMap[section.key] = DocConfig.checkReadOnly(section.key, currentCompilationType);
    }
  }

  getSectionVisibility(doc: CompilationModel, section: string) {
    return doc.visibleFields.includes(section)
  }
  
  setSectionVisible(doc: CompilationModel, section: string, visible = true) {
    if (visible && !doc.visibleFields.includes(section)) {
      doc.visibleFields.push(section);
    } else if (!visible && doc.visibleFields.includes(section)) {
      doc.visibleFields.splice(doc.visibleFields.indexOf(section), 1);
    }
  }

          // this.addressOptions = [{ id: null, name: 'Select address', address: '' }, ...this.addresses.map(m => {
          //   const type = ['', 'General', 'Legal', 'Invoice', 'Delivery', 'Employee', 'Pick up', 'Ex employee'][m.type];
          //   return ({ id: m._id, name: `${m.title} (${type})`, address: this.contactUtil.getAddress(m, DocConfig.get('defaultAddress').fields) })
          // })];
          // this.addressOptions2 = [{ id: null, name: 'Select address', address: '' }, ...this.addresses2.map(m => {
          //   return ({ id: m._id, name: `${m.title}`, address: this.contactUtil.getAddress(m, DocConfig.get('invoiceSendToAddress').fields) })
          // })]
      
          // this.addressOptionId = (this.addressOptions.find(f => 
          //   f.address && this.address
          //   && f.address.replace(/\s+/g, '') === this.address.replace(/Attn: [^\n]+/, '').replace(/\s+/g, '')
          // ) || {} as any).id || null;
  
  getAddressContact(recipient: BusinessModel, address: string) {
    const business = this._businesses.find(f => mongoId(f) === mongoId(recipient));
    const businessContacts = business?.contacts.map(c => this._contacts.find(f => mongoId(f) === mongoId(c))) || [];
    const addresses = businessContacts.map(c => ({ contact: this._contacts.find(f => mongoId(f) === mongoId(c)), address: (new ContactUtil(this)).getAddress(c, DocConfig.get('defaultAddress').fields) }));
    return addresses.find(f => f.address?.replace(/\s+/g, '') === address?.replace(/\s+/g, ''))?.contact || null;
  }

  getDeliveryAndTerms(businessModel: BusinessModel, contact: ContactModel = null) {
    const business = this._businesses.find(f => mongoId(f) === mongoId(businessModel));
    const delivery = business?.contacts?.find(c => c.type == EContactType.Delivery);
    const general = business?.contacts?.find(c => c.type == EContactType.General);
    const incoterms = (contact || delivery || general)?.fields?.find(f => +f.type === EContactFieldType.Incoterms);
    const place = (contact || delivery || general)?.fields?.find(f => +f.type === EContactFieldType.IncotermsPlace);
    const country = (contact || delivery || general)?.fields?.find(f => +f.type === EContactFieldType.IncotermsCountry);
    let paymentText = contact?.fields?.find(f => +f.type === EContactFieldType.PaymentText);
    let terms = contact?.fields?.find(f => +f.type === EContactFieldType.PaymentConditions);
    if (!terms) {
      paymentText = general?.fields?.find(f => +f.type === EContactFieldType.PaymentText);
      terms = general?.fields?.find(f => +f.type === EContactFieldType.PaymentConditions);
    }
    const vat = (contact || general)?.fields?.find(f => +f.type === EContactFieldType.VAT_Number);
    const ret = { incoterms, place, country, paymentText, terms, vat }
    return ret;
  }

  getBusiness(business: BusinessModel) {
    return this.businesses.find(f => mongoId(f) === mongoId(business));
  }

  getBusinessManagerUser(business: BusinessModel) {
    const _business = this._businesses.find(f => mongoId(f) === mongoId(business));
    const user = this._users.find(u => mongoId(u.contact) === mongoId(_business?.manager))
    return user;
  }

  // internal business name
  getBusinessName(business: BusinessModel | string): string {
    return this.businesses.find(f => mongoId(f) === mongoId(business))?.name || '';
  }

  // official business name
  getBusinessAddressName(business: BusinessModel | string): string {
    business = this.businesses.find(f => mongoId(f) === mongoId(business));
    const name = business.contacts.find(f => +f.type === EContactType.General)?.title ?? '';
    return name;
  }

  getObicString(compilation: CompilationModel, tree: TreeService) {
    if (['OBIC'].includes(CompilationTypeData.get(compilation.type)?.code) && compilation.nameOverride) return;
    if (['OBIC', 'PFSPO', 'SPO'].includes(CompilationTypeData.get(compilation.type)?.code)) {
      if (tree.linkedObic) compilation = tree.linkedObic;
      return compilation.lineItems.map(m => m.tbCode)?.filter(notNull => notNull && notNull.length)?.join(', ');
    }
  }

  sanitizeHtml(html: string) {
    // return html && this.domSanitizer.bypassSecurityTrustHtml(html);
    return this.domSanitizer.sanitize(SecurityContext.HTML,this.domSanitizer.bypassSecurityTrustHtml(html));

  }

  date(date: Date) {
    // return (new Date(date)).toISOString().replace(/T.*/, '');
    return sanitizeDateToString(date);
  }

  copyToClipboard(toCopy: string): void {
    // const createCopy = (e: ClipboardEvent) => {
    //   e.clipboardData.setData('text/plain', toCopy);
    //   e.preventDefault();
    // };
    // document.addEventListener('copy', createCopy );
    // document.execCommand('copy');
    // document.removeEventListener('copy', createCopy );
    this.clipboard.copy(toCopy);
  }

  copyHtmlToClipboard(html: string): void {

    var container = document.createElement('div')
    container.innerHTML = html
  
    container.style.position = 'fixed'
    container.style.pointerEvents = 'none'
    container.style.opacity = '0'
  
    // Detect all style sheets of the page
    // var activeSheets = Array.prototype.slice.call(document.styleSheets)
    //   .filter(function (sheet) {
    //     return !sheet.disabled
    //   })
  
    document.body.appendChild(container)
    window.getSelection().removeAllRanges()
  
    var range = document.createRange()
    range.selectNode(container)
    window.getSelection().addRange(range)
  
    document.execCommand('copy')
    // for (var i = 0; i < activeSheets.length; i++) activeSheets[i].disabled = true
    // document.execCommand('copy')
    // for (var i = 0; i < activeSheets.length; i++) activeSheets[i].disabled = false
    document.body.removeChild(container)
  }

  getPackingConfig(id: string = null) {
    const palletDetails = this._compilationData.packingStore?.find(f => f.id === 'palletDetails')?.data;
    const boxDetails = this._compilationData.packingStore?.find(f => f.id === 'boxDetails')?.data;
    const finalPacking = this._compilationData.packingStore?.find(f => f.id === 'finalPacking')?.data;

    // console.log('getPackingConfig', id)
    if (!id) return ({ palletDetails, boxDetails, finalPacking }) as any

    for (const el of [...palletDetails, ...boxDetails, ...finalPacking]) {
      // if (el === id) console.log('getPackingConfig', el.id === id, el)
      if (el.id === id) return el;
    }

  }

  getIssuerFromCode(code: string, asNumber = false) {
    const match = code.match(/^.*(NRK.).*$/);
    return match && (asNumber ? IssuerData.get(match[1]?.toLocaleLowerCase()).id : match[1]?.toLocaleLowerCase())
  }

  /**
   * 1) Das Dispatch date ist die maßgebliche Periode. 
   * 2) wenn Issue date > dispatch date UND das issue date fällt in eine anders Periode, gilt die andere Periode
   * fiscal period eg.: 2024-10-01 to 2025-09-30
   * @param doc 
   * @returns 
   */
  fiscalYearUpdate(doc: CompilationModel) {

    function getPeriod(date: Date) {
      const year = date.getFullYear();
      const startOfPeriod = new Date(year, 9, 1); // October 1st
      const endOfPeriod = new Date(year + 1, 8, 30); // September 30th of the next year
  
      if (date >= startOfPeriod && date <= endOfPeriod) {
          return year;
      } else {
          return year - 1;
      }
    }

    if (!doc.vieDispatchDate) return;

    const issueDateObj = new Date(doc.issueDate);
    const dispatchDateObj = new Date(doc.vieDispatchDate);

    const dispatchPeriod = getPeriod(dispatchDateObj);
    const issuePeriod = getPeriod(issueDateObj);

    if (issueDateObj > dispatchDateObj && dispatchPeriod !== issuePeriod) {
      // console.log(`-----> fiscal year update ${doc.code}: ${doc.issueDate} > ${doc.vieDispatchDate} = ${issuePeriod}`);
      doc.fiscalYear = issuePeriod;
    } else {
      // console.log(`..................${doc.code}: ${doc.vieDispatchDate} = ${dispatchPeriod}`)
      doc.fiscalYear = dispatchPeriod;
    }

  }

  paymentPlanHasInvoices(doc: CompilationModel) {
    return !isEmpty(doc.paymentPlan?.items?.find(f => f.invoices?.length > 0));
  }


  getCustomerInvoiceAddress(business: BusinessModel) {
    return (new ContactUtil(this)).getAddressFromBusiness(
      mongoId(business), 
      DocConfig.get('invoiceSendToAddress').fields, 
      EContactType.Invoice,
      false
    )
  }

  calcData(compilation: CompilationModel | CompilationModel[] | OcSpoLineItemMap[], fx = { toEUR: false, date: null }, quantityFactor = 1) {
    
    if (!compilation) return {};

    if (Array.isArray(compilation)) {
      return (compilation as []).reduce((a: any, c: any) => {
        if (c) {
          const d = this.calcData(c.doc ? c.doc : c, fx, c.doc ? c.quantityFactor : quantityFactor);
          a.sumTotal        = (a.sumTotal        ?? 0) + d.sumTotal;
          a.partial         = (a.partial         ?? 0) + d.partial;
          a.discounted      = (a.discounted      ?? 0) + d.discounted;
          a.subTotalNet     = (a.subTotalNet     ?? 0) + d.subTotalNet;
          a.totalNet        = (a.totalNet        ?? 0) + d.totalNet;
          a.progressPayment = (a.progressPayment ?? 0) + d.progressPayment;
          a.deductible      = (a.deductible      ?? 0) + d.deductible;
        }
        return a;
      }, {});
    }

    console.log('calc data', compilation.code, fx.toEUR)

    const deductibleTotal = compilation.deductibleItems?.reduce((sum, item) => sum + (1 * item.value), 0) ?? 0;
    const ppInvoicesTotal = compilation.paymentPlan?.items?.reduce((sum, item) => {
      return sum + (item?.invoices?.reduce((invSum, inv) => invSum + (inv?.net || 0), 0) || 0);
    }, 0) || 0;

    let vatItems: { vat: number; sum: number; partialVat: number; discountedVat: number }[] = [];

    if (compilation.lineItems?.length) {
      const vatMap = new Map<number, { vat: number; sum: number; partialVat: number; discountedVat: number }>();

      for (const li of compilation.lineItems) {
        // fix quantity rounding issue (10 decimals)
        li.quantity = Number((li.quantity ?? 0).toFixed(10));

        const vat = +li.VATPercent;
        if (vat < 0) continue;

        // ensure entry exists
        if (!vatMap.has(vat)) {
          vatMap.set(vat, { vat, sum: 0, partialVat: 0, discountedVat: 0 });
        }

        const entry = vatMap.get(vat)!;
        const base = li.quantity * quantityFactor * li.piecePrice;

        entry.sum += base;
        entry.partialVat += base * (li.invoicePercent / 100);
        entry.discountedVat += base * ((100 - li.discount) / 100);
      }

      vatItems = Array.from(vatMap.values());
    }

    // Add deductible + invoices to first VAT item
    if (vatItems.length > 0) {
      const extra = deductibleTotal + ppInvoicesTotal;
      Object.assign(vatItems[0], {
        sum: vatItems[0].sum + extra,
        partialVat: vatItems[0].partialVat + extra,
        discountedVat: vatItems[0].discountedVat + extra,
      });
    }
        
    const subTotal = compilation.lineItems?.reduce((sum, item) => sum + (item.quantity * quantityFactor * item.piecePrice), 0)
    const partialSubTotal = compilation.lineItems?.reduce((sum, item) => sum + (item.quantity * quantityFactor * item.piecePrice * (item.invoicePercent/100)), 0)
    const discountedSubTotal = compilation.lineItems?.reduce((sum, item) => sum + (item.quantity * quantityFactor * item.piecePrice * ((100-item.discount)/100)), 0)
    const netTotal = subTotal +  deductibleTotal;
    const partialNetTotal = partialSubTotal + deductibleTotal;
    const discountedNetTotal = discountedSubTotal + deductibleTotal;
    const ppNetTotal = netTotal + ppInvoicesTotal;
    const ppPartialNetTotal = partialNetTotal + ppInvoicesTotal;
    const ppDiscountedNetTotal = discountedNetTotal + ppInvoicesTotal;
    const vatTotal = vatItems?.reduce((sum, item) => sum + (item.sum * item.vat / 100), 0);
    const partialVatTotal = vatItems?.reduce((sum, item) => sum + (item.partialVat * (item.vat / 100)), 0);
    const discountedVatTotal = vatItems?.reduce((sum, item) => sum + (item.discountedVat * (item.vat / 100)), 0);
    const sumTotal = ppNetTotal + vatTotal;
    const partialSumTotal = ppPartialNetTotal + partialVatTotal;
    const discountedSumTotal = ppDiscountedNetTotal + discountedVatTotal;

    const isPartialPayment = compilation.invoicePayment === 1 || compilation.invoicePayment === 2 // down payment || partial payment
    const isDiscount = !isPartialPayment && (compilation.lineItems || []).find(li => li.discount > 0);

    const eur = (value: number) => {
      if (fx.toEUR) {
        value = toEUR(fx.date ?? compilation.issueDate, compilation.currency, value, this);
      }
      return value;
    }
    
    return ({ 
      sum: eur(sumTotal), 
      partial: eur(partialSumTotal), 
      discounted: eur(discountedSumTotal),
      subTotalNet: isPartialPayment
        ? eur(partialSubTotal)
        : isDiscount
          ? eur(discountedSubTotal)
          : eur(subTotal),
      totalNet: isPartialPayment
        ? eur(partialSumTotal)
        : isDiscount
          ? eur(discountedSumTotal)
          : eur(sumTotal),
      progressPayment: eur(ppInvoicesTotal),
      deductible: eur(deductibleTotal)
    })
  }

  getPositionNumberFormat(compilation: CompilationModel) {
    //console.log('recipient', this.compilation.recipient, this.data.businesses.find(f => mongoId(f) === mongoId(this.compilation.recipient)));
    const business = this.businesses.find(f => mongoId(f) === mongoId(compilation.recipient));
    return business?.positionNumberFormat || '%02d';
  }

  addDateToHistory(date: ErpDateClassType, user: string) {
    if (!date?.history?.length && !date?.val) return;
    const historyVal = moment(date.history[0]?.val).isValid() ? new Date(date.history[0]?.val) : undefined;
    const currentVal = moment(date.val).isValid() ? new Date(date.val) : undefined;
    // console.log('add date to history', historyVal, currentVal, date, user);
    if (historyVal?.getTime() !== currentVal?.getTime()) {
      date.history.unshift({
        updatedAt: new Date(),
        val: currentVal,
        remark: date.remark,
        user: user,
      })
      date.remark = undefined;
    } else if (date.history.length && date.remark) {
      date.history[0].remark = date.remark;
      date.remark = undefined;
    }
  }

  updateDateHistory(doc: CompilationModel, user: string) {
    const sec = DocConfig.getSections();
    const dates = Object.keys(sec).filter(k => ['displayDate', 'reportDate'].includes(sec[k].type));
    for (const date of dates) {
      const jsDate = doc[date] && new Date(doc[date]);
      if (jsDate && isErpDate(doc.erpDate[date])) {
        console.log('-----------before', date, doc[date], jsDate, doc.erpDate);
        doc.erpDate[date].val = jsDate;
        this.addDateToHistory(doc.erpDate[date], user);
        if (sec[date].displayRef) {
          doc.erpDate[sec[date].displayRef] = structuredClone(doc.erpDate[date]);
        }
        console.log('after', date, jsDate, doc.erpDate);
      }
    }
    // for (const erpDate of Object.values(doc.erpDate)) {
    //   if (isErpDate(erpDate)) {
    //     this.addDateToHistory(erpDate, user);
    //   }
    // }
  }

  // checkShelfLifeForCompilation(doc: CompilationModel, boxes: SpoBoxModel[]) {

  //   // console.log('packing length', doc.code, doc.packingConditions?.length)

  //   for (const pallet of doc.packingConditions) {
  //     // if (checkShelfLife(doc, pallet)) {
  //     //   console.log('check shelf life', doc.code, true);
  //     // }
  //     if (this.checkShelfLife(doc, pallet, boxes)) return true;
  //   }

  //   return false;

  // }

  // async checkShelfLife(doc: any, pallet: PackingConditions, boxes: SpoBoxModel[]) {

  //   if (!boxes?.length) await this.loadSpoBoxes();
  //   await this.loadProducts();

  //   console.log('boxes.length pre', boxes?.length, pallet.palId);

  //   // boxes = Object.values(boxes).filter(f => f.palletRef === pallet.palId);
  //   boxes = Object.values(boxes || {})?.filter(f => f.palletRef === pallet.palId);

  //   console.log('boxes.length', boxes);

  //   if (!boxes?.length) {
  //     return false;
  //   }

  //   console.log('boxes.length post', doc, boxes?.length, pallet.lineItemIdx);

  //   const fpId = mongoId(doc.lineItems[pallet.lineItemIdx]?.fp);
  //   const fp = this.finishedProducts.find(f => mongoId(f) && mongoId(fpId));
  //   const product = fp?.product as BaseProductModel;
  //   // let shelfLife = product.shelfLife5Deg || product.shelfLife10Deg || product.shelfLife20Deg;

  //   console.log('boxes fp,', fpId, fp, product)

  //   if (!fp || !product) return false;

  //   const standard = fp.boxLabelPriority === EBoxLabelPriority.Standard5Deg || fp.boxLabelPriority === EBoxLabelPriority.Standard10Deg || fp.boxLabelPriority === EBoxLabelPriority.Standard20Deg;
  //   const column = fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn5Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn10Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn20Deg;
  //   const calc = fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation5Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation10Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation20Deg;
  //   const deg5 = fp.boxLabelPriority === EBoxLabelPriority.Standard5Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn5Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation5Deg;
  //   const deg10 = fp.boxLabelPriority === EBoxLabelPriority.Standard10Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn10Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation10Deg;
  //   const deg20 = fp.boxLabelPriority === EBoxLabelPriority.Standard20Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryColumn20Deg || fp.boxLabelPriority === EBoxLabelPriority.ExpiryCalculation20Deg;

  //   // console.log(doc.code, boxes.length, shelfLife);
  //   const shelfLife = deg5
  //     ? product.shelfLife5Deg
  //     : deg10
  //       ? product.shelfLife10Deg
  //       : deg20
  //         ? product.shelfLife20Deg
  //         : product.shelfLife5Deg || product.shelfLife10Deg || product.shelfLife20Deg;


  //   // console.log('shelf life', doc.code, shelfLife);

  //   const dates = boxes.map(box => {
  //     if (box.shelfLifeDate && column) return moment(box.shelfLifeDate);
  //     const boxDetails = doc.lineItems[pallet.lineItemIdx].pfspo.flatMap((m: AssociatedPfspo) => m.doc.lineItems).flatMap(li => li.boxDetails);
  //     const prodDates = boxDetails.filter(f => f.boxId === box.boxId).map(m => moment(m.prodDate));
  //     const minProdDate = moment.min(prodDates);
  //     if (!minProdDate || !minProdDate.isValid()) return null;
  //     return minProdDate.add(shelfLife, 'months');
  //   }).filter(notNull => notNull);

  //   const minShelfLifeDate = moment.min(dates);
  //   if (!minShelfLifeDate.isValid()) return false;
  //   const months = minShelfLifeDate?.diff(moment(doc.vieDispatchDate), 'months', true);
  //   // if (doc.code ==='DNNRKA240327') {
  //   //   console.log('------------->boxes', pallet.palId, doc.code, Object.values(DbCache.spoBoxes).length, months, minShelfLifeDate.toISOString(), shelfLife, months < 6);
  //   //   console.log('-', boxes.length, dates);
  //   // }
  //   return shelfLife !== undefined && shelfLife !== null ? months < 6 : false;
  // }

    

}
